import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/layout/Navbar';
import CalendarioOrdenesTrabajo from '../components/ordenesTrabajo/CalendarioOrdenesTrabajo';
import ActividadesOrdenTrabajoList from '../components/ordenesTrabajo/ActividadesOrdenTrabajoList';
import { calcularDuracionMinutos, formatDuration } from '../utils/timeHelpers';
import apiService from '../services/apiService';
import { useToast } from '../components/contexts/ToastContext';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';

/**
 * Página de calendario de órdenes de trabajo
 *
 * Muestra las OTs en vista calendario mensual con sidebar de actividades.
 *
 * @returns {JSX.Element}
 */
function CalendarioOT() {
  const [ordenesTrabajo, setOrdenesTrabajo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrdenTrabajo, setSelectedOrdenTrabajo] = useState(null);
  const [actividadesRefreshTrigger, setActividadesRefreshTrigger] = useState(0);
  const { showToast } = useToast();

  /**
   * Carga todas las OTs para el calendario
   * @returns {Promise<void>}
   */
  const fetchOrdenesTrabajo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getOrdenesTrabajo({ limit: 1000 });
      if (response.data) {
        setOrdenesTrabajo(response.data);
      }
    } catch (error) {
      console.error('Error fetching OTs para calendario:', error);
      showToast('Error al cargar calendario', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchOrdenesTrabajo();
  }, [fetchOrdenesTrabajo]);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Calendario OTs' }
        ]} />

        <CalendarioOrdenesTrabajo
          ordenesTrabajo={ordenesTrabajo}
          onOrdenTrabajoClick={(ot) => setSelectedOrdenTrabajo(ot)}
          loading={loading}
        />
      </div>

      {/* Sidebar de Actividades */}
      {selectedOrdenTrabajo && (
        <>
          <div
            className="sidebar-overlay"
            onClick={() => setSelectedOrdenTrabajo(null)}
          />
          <div className="sidebar-responsive-lg active bg-sidebar">
            <div className="sidebar-header">
              <h3 className="sidebar-title">
                Actividades: {selectedOrdenTrabajo.equipo?.codigo}
                {selectedOrdenTrabajo.fecha_inicio && (
                  <span className="badge badge-secondary margin-left-xs font-size-xs">
                    <i className="fas fa-clock"></i>{' '}
                    {selectedOrdenTrabajo.fecha_fin
                      ? formatDuration(calcularDuracionMinutos(selectedOrdenTrabajo.fecha_inicio, selectedOrdenTrabajo.fecha_fin))
                      : formatDuration(calcularDuracionMinutos(selectedOrdenTrabajo.fecha_inicio, new Date()))
                    }
                  </span>
                )}
              </h3>
              <button
                className="sidebar-close"
                onClick={() => setSelectedOrdenTrabajo(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <ActividadesOrdenTrabajoList
              ordenTrabajoId={selectedOrdenTrabajo.id}
              ordenTrabajoEstado={selectedOrdenTrabajo.estado}
              plantillaId={selectedOrdenTrabajo.plantilla_mantenimiento_id}
              onRefresh={() => setActividadesRefreshTrigger(prev => prev + 1)}
              refreshTrigger={actividadesRefreshTrigger}
            />
          </div>
        </>
      )}
    </>
  );
}

export default CalendarioOT;
